import React from 'react';
import { Check, Star, Sparkles } from 'lucide-react';

interface MobilePackCardCarouselProps {
  name: string;
  price: string;
  features: string[];
  isPopular?: boolean;
  onClick?: () => void;
}

const MobilePackCardCarousel: React.FC<MobilePackCardCarouselProps> = ({
  name,
  price,
  features,
  isPopular = false,
  onClick,
}) => {
  return (
    <div
      onClick={onClick}
      className={`relative bg-white rounded-3xl shadow-xl overflow-hidden h-80 transition-all duration-300 active:scale-95 ${
        isPopular
          ? 'border-2 border-amber-400 shadow-amber-200'
          : 'border border-gray-100'
      }`}
    >
      {/* Badge Popular */}
      {isPopular && (
        <div className="absolute top-0 right-0 z-10">
          <div className="bg-gradient-to-br from-amber-500 to-amber-600 text-white px-4 py-1.5 rounded-bl-2xl flex items-center gap-1.5 shadow-lg">
            <Star className="w-3.5 h-3.5 fill-current" />
            <span className="text-xs font-bold">POPULAIRE</span>
          </div>
        </div>
      )}

      {/* Gradient Background */}
      <div
        className={`absolute inset-0 ${
          isPopular
            ? 'bg-gradient-to-br from-amber-50 via-white to-amber-50/30'
            : 'bg-gradient-to-br from-gray-50 via-white to-gray-50'
        }`}
      />

      {/* Content */}
      <div className="relative h-full flex flex-col p-6">
        {/* Header */}
        <div className="mb-4">
          <h3 className="text-2xl font-bold text-gray-900 mb-2">{name}</h3>
          <div className="flex items-baseline gap-2">
            <span
              className={`text-3xl font-bold ${
                isPopular
                  ? 'bg-gradient-to-r from-amber-600 to-amber-500 bg-clip-text text-transparent'
                  : 'text-gray-900'
              }`}
            >
              {price}
            </span>
            {price !== 'Sur devis' && (
              <span className="text-sm text-gray-500 font-medium">CFA</span>
            )}
          </div>
        </div>

        {/* Features */}
        <ul className="space-y-2.5 flex-1 mb-4">
          {features.slice(0, 4).map((feature, index) => (
            <li key={index} className="flex items-start gap-2.5">
              <div
                className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5 ${
                  isPopular
                    ? 'bg-gradient-to-br from-amber-500 to-amber-600'
                    : 'bg-gradient-to-br from-gray-700 to-gray-600'
                }`}
              >
                <Check className="w-3 h-3 text-white" strokeWidth={3} />
              </div>
              <span className="text-sm text-gray-700 leading-snug">
                {feature}
              </span>
            </li>
          ))}
          {features.length > 4 && (
            <li className="text-sm text-gray-500 italic pl-7">
              + {features.length - 4} autre{features.length - 4 > 1 ? 's' : ''}
            </li>
          )}
        </ul>

        {/* CTA Button */}
        <button
          className={`w-full py-3.5 rounded-xl font-semibold text-sm transition-all duration-200 flex items-center justify-center gap-2 ${
            isPopular
              ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-white shadow-lg shadow-amber-500/30 hover:shadow-xl'
              : 'bg-gradient-to-r from-gray-900 to-gray-800 text-white shadow-md hover:shadow-lg'
          }`}
        >
          {isPopular && <Sparkles className="w-4 h-4" />}
          Choisir ce pack
        </button>
      </div>

      {/* Decorative accent */}
      <div
        className={`absolute bottom-0 left-0 w-40 h-40 rounded-tr-full opacity-10 ${
          isPopular
            ? 'bg-gradient-to-tr from-amber-400 to-transparent'
            : 'bg-gradient-to-tr from-gray-400 to-transparent'
        }`}
      />
    </div>
  );
};

export default MobilePackCardCarousel;
